// =====================================================
// PAGE: ContactDetailPage
// Description: Page dédiée pour voir et éditer les détails d'un contact
// =====================================================

import { Globe, Loader2, MailWarning, Phone, Plus, Tag, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AssignUserButton,
  ContactCard,
  ContactInfoForm,
  FormSubmissionsSection,
} from '../components/contact';
import { CrewsList } from '../components/crew';
import { FilesList } from '../components/files';
import {
  AssignCrewModal,
  ConfirmDeleteContactDialog,
  CreateTaskModal,
  LogCallModal,
  SendEmailModal,
} from '../components/modals';
import { DetailPageLayout } from '../components/page';
import { PaymentSection } from '../components/payment/PaymentSection';
import { Timeline } from '../components/timeline';
import { Badge, toast } from '../components/ui';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useContactActions } from '../hooks/useContactActions';
import { useCrewActions } from '../hooks/useCrewActions';
import { useCrewsForContact } from '../hooks/useCrewsForContact';
import { usePermissions } from '../hooks/usePermissions';
import { useProspects } from '../hooks/useProspects';
import { formatTimeAgo } from '../lib/date-utils';
import { ROUTES } from '../lib/routes/constants';
import type { Contact } from '../types/contact';
import {
  CONTACT_STATUS_COLORS,
  CONTACT_STATUS_LABELS,
  EMAIL_INVALID_REASON_LABELS,
} from '../types/contact';

// Configuration des onglets
const TABS = [
  { id: 'globale', label: 'Globale' },
  { id: 'fichiers', label: 'Fichiers' },
];

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // ✅ Hooks optimisés : pas de chargement automatique de toutes les données
  const { getContactById, updateContact, unassignUser } = useContactActions();
  const { addCrewMember, getCrewById } = useCrewActions();
  const { canManageAssignments, canDeleteEntity, canSendEmails } = usePermissions();
  const { deleteProspectByContactAndProduct } = useProspects({ mode: 'server' });

  // Utiliser le hook optimisé pour charger uniquement les crews du contact
  const { crews, loading: crewsLoading, refresh: refreshCrews } = useCrewsForContact(id);

  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('globale');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const submitFormRef = useRef<(() => void) | null>(null);
  const [isLogCallModalOpen, setIsLogCallModalOpen] = useState(false);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isAssignCrewModalOpen, setIsAssignCrewModalOpen] = useState(false);

  // ✅ OPTIMISATION : Ne plus appeler useTimeline, on récupère les events du Timeline
  const [timelineEvents, setTimelineEvents] = useState<any[]>([]);
  const refreshTimelineRef = useRef<(() => Promise<void>) | null>(null);

  // Trouver le dernier appel
  const lastCall = useMemo(() => {
    const callEvents = timelineEvents.filter((event) => event.event_type === 'call_logged');
    return callEvents.length > 0 ? callEvents[0] : null; // Déjà triés par created_at DESC
  }, [timelineEvents]);

  // Callbacks memorisés pour le Timeline (éviter les re-renders en boucle)
  const handleEventsLoaded = useCallback((events: any[]) => {
    setTimelineEvents(events);
  }, []);

  const handleRefreshReady = useCallback((fn: () => Promise<void>) => {
    refreshTimelineRef.current = fn;
  }, []);

  // Charger le contact
  useEffect(() => {
    async function loadContact() {
      if (!id) return;

      setLoading(true);
      const data = await getContactById(id);
      setContact(data);
      setLoading(false);
    }

    loadContact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // On ne dépend que de l'ID, pas de getContactById

  // Les équipages sont maintenant chargés automatiquement par useCrewsForContact
  // Plus besoin de useEffect séparé !

  // Gérer la mise à jour
  async function handleUpdateContact(
    data:
      | {
          first_name: string;
          last_name: string;
          email: string;
          phone?: string;
          school_id?: string | null;
          notes?: string | null;
          product_ids?: string[];
          // Adresse
          address?: string | null;
          city?: string | null;
          postal_code?: string | null;
          country?: string | null;
          //Birthdate
          birth_date?: string | null;
          // Origine
          origin_id?: string | null;
          origin_info?: string | null;
          // Statut étudiant
          is_not_student?: boolean;
          socio_professional_category?: string | null;
          // Newsletter
          newsletter_opt_in?: boolean;
        }
      | { product_ids: string[] }
  ): Promise<boolean> {
    if (!contact) return false;

    try {
      // Préparer les données en supprimant les valeurs null pour les champs optionnels
      const updateData: any = {};

      // Si c'est juste un update de produits
      if ('first_name' in data) {
        // Update complet des infos
        updateData.first_name = data.first_name;
        updateData.last_name = data.last_name;
        updateData.email = data.email;

        if (data.phone !== undefined) updateData.phone = data.phone || null;
        if (data.school_id !== undefined) updateData.school_id = data.school_id || null;
        if (data.notes !== undefined) updateData.notes = data.notes || null;
        if (data.product_ids !== undefined) updateData.product_ids = data.product_ids;
        // Adresse
        if (data.address !== undefined) updateData.address = data.address || null;
        if (data.city !== undefined) updateData.city = data.city || null;
        if (data.postal_code !== undefined) updateData.postal_code = data.postal_code || null;
        if (data.country !== undefined) updateData.country = data.country || null;
        // Date de naissance
        if (data.birth_date !== undefined) updateData.birth_date = data.birth_date || null;
        // Origine
        if (data.origin_id !== undefined) updateData.origin_id = data.origin_id || null;
        if (data.origin_info !== undefined) updateData.origin_info = data.origin_info || null;
        // Statut étudiant
        if (data.is_not_student !== undefined) updateData.is_not_student = data.is_not_student;
        if (data.socio_professional_category !== undefined)
          updateData.socio_professional_category = data.socio_professional_category || null;
        // Newsletter
        if (data.newsletter_opt_in !== undefined)
          updateData.newsletter_opt_in = data.newsletter_opt_in;
      } else {
        // Update uniquement des produits
        updateData.product_ids = data.product_ids;
      }

      const success = await updateContact(contact.id, updateData);

      if (success) {
        // Recharger le contact pour avoir les données à jour
        const updatedContact = await getContactById(contact.id);
        if (updatedContact) {
          setContact(updatedContact);
        }
        // Rafraîchir la timeline pour refléter les changements
        if (refreshTimelineRef.current) await refreshTimelineRef.current();
        toast.success('Contact mis à jour avec succès');
        return true;
      } else {
        toast.error('Erreur lors de la mise à jour du contact');
        return false;
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Erreur lors de la mise à jour du contact');
      return false;
    }
  }

  // Gérer l'assignation d'un utilisateur
  async function handleAssignUser(userId: string | null): Promise<void> {
    if (!contact) return;

    try {
      const success = await updateContact(contact.id, { assigned_to: userId });

      if (success) {
        // Recharger le contact pour avoir les données à jour
        const updatedContact = await getContactById(contact.id);
        if (updatedContact) {
          setContact(updatedContact);
        }
        // Rafraîchir la timeline pour refléter le changement
        if (refreshTimelineRef.current) await refreshTimelineRef.current();
        toast.success('Utilisateur assigné avec succès');
      } else {
        toast.error("Erreur lors de l'assignation");
      }
    } catch (error) {
      console.error('Error assigning user:', error);
      toast.error("Erreur lors de l'assignation");
    }
  }

  // Gérer le retrait d'assignation
  async function handleUnassignUser(): Promise<void> {
    if (!contact) return;

    try {
      const success = await unassignUser(contact.id);

      if (success) {
        // Recharger le contact pour avoir les données à jour
        const updatedContact = await getContactById(contact.id);
        if (updatedContact) {
          setContact(updatedContact);
        }
        // Rafraîchir la timeline pour refléter le changement
        if (refreshTimelineRef.current) await refreshTimelineRef.current();
        toast.success('Assignation retirée');
      } else {
        toast.error("Erreur lors du retrait de l'assignation");
      }
    } catch (error) {
      console.error('Error unassigning user:', error);
      toast.error("Erreur lors du retrait de l'assignation");
    }
  }

  // Handler succès modal appel
  async function handleCallSuccess() {
    // Petit délai pour s'assurer que la transaction DB est terminée
    await new Promise((resolve) => setTimeout(resolve, 150));
    // Rafraîchir les événements de timeline pour mettre à jour le dernier appel
    if (refreshTimelineRef.current) await refreshTimelineRef.current();
  }

  // Handler succès modal email
  async function handleEmailSuccess() {
    // Rafraîchir les événements de timeline
    if (refreshTimelineRef.current) await refreshTimelineRef.current();
  }

  // Handler succès modal tâche
  async function handleTaskSuccess() {
    // Rafraîchir les événements de timeline
    if (refreshTimelineRef.current) await refreshTimelineRef.current();
  }

  // Gérer l'assignation d'un équipage
  async function handleAssignCrew(crewId: string) {
    if (!contact || !id) return;

    try {
      // Vérifier le nombre d'équipages du contact
      if (crews.length >= 2) {
        toast.error('Ce contact fait déjà partie de 2 équipages');
        return;
      }

      // Récupérer l'équipage pour déterminer le rôle automatiquement
      const crew = await getCrewById(crewId);
      if (!crew) {
        toast.error('Équipage non trouvé');
        return;
      }

      // Déterminer le rôle : Pilote si pas de pilote, sinon Copilote
      const hasPilot = crew.members?.some((m) => m.role === 'pilot');
      const role = hasPilot ? 'copilot' : 'pilot';

      const success = await addCrewMember(crewId, id, role);
      if (success) {
        const roleLabel = role === 'pilot' ? 'Pilote' : 'Copilote';
        toast.success(`Contact ajouté comme ${roleLabel}`);

        // Utiliser le crew déjà récupéré pour obtenir son produit
        if (crew.product_id) {
          // ✅ Supprimer le prospect correspondant au produit de l'équipage
          await deleteProspectByContactAndProduct(id, crew.product_id);

          // Si l'équipage a un produit, l'ajouter au contact si pas déjà associé
          const currentProductIds = contact.products?.map((p) => p.id) || [];

          // Vérifier que le produit n'est pas déjà associé
          if (!currentProductIds.includes(crew.product_id)) {
            const updatedProductIds = [...currentProductIds, crew.product_id];
            // eslint-disable-next-line no-console
            console.log('Adding crew product to contact', {
              contactId: id,
              crewProductId: crew.product_id,
              updatedProductIds,
            });
            await updateContact(id, { product_ids: updatedProductIds } as any);

            // Recharger le contact pour avoir les produits à jour
            const updatedContact = await getContactById(id);
            // eslint-disable-next-line no-console
            console.log('Contact reloaded after adding crew product', {
              contactId: id,
              products: updatedContact?.products,
            });
            if (updatedContact) {
              setContact(updatedContact);
            }
          }
        }

        // Recharger les équipages
        await refreshCrews();
        // Rafraîchir la timeline
        if (refreshTimelineRef.current) await refreshTimelineRef.current();
      } else {
        toast.error("Erreur lors de l'ajout à l'équipage");
      }
    } catch (error) {
      console.error('Error assigning crew:', error);
      toast.error("Erreur lors de l'assignation");
    }
  }

  // Obtenir l'heure locale
  function getLocalTime() {
    const now = new Date();
    return (
      now.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      }) + ' heure locale'
    );
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-zinc-500">Chargement...</p>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-zinc-500">Contact introuvable</p>
        <Button onClick={() => navigate(ROUTES.DATA.CONTACTS.LIST)} variant="outline">
          Retour à la liste
        </Button>
      </div>
    );
  }

  // Breadcrumbs pour le header
  const breadcrumbs = [
    { label: 'Data', href: ROUTES.DATA.CONTACTS.LIST },
    { label: 'Contacts', href: ROUTES.DATA.CONTACTS.LIST },
    { label: `${contact.first_name} ${contact.last_name}` },
  ];

  // Sous-titre avec badges et infos
  const subtitle = (
    <>
      {/* Badge de statut */}
      <Badge
        className={`flex items-center gap-1 ${CONTACT_STATUS_COLORS[contact.status].bg} ${CONTACT_STATUS_COLORS[contact.status].text} border ${CONTACT_STATUS_COLORS[contact.status].border}`}
      >
        <Tag className="h-3 w-3" />
        {CONTACT_STATUS_LABELS[contact.status]}
      </Badge>
      {/* Badge délivrabilité : email flaggé invalide par le webhook Mailjet (TICKET-CRM-13) */}
      {contact.email_invalid_at && (
        <Badge
          className="flex items-center gap-1 border border-red-500 bg-red-50 text-red-700"
          title={`Email non délivré (${EMAIL_INVALID_REASON_LABELS[contact.email_invalid_reason ?? 'hard_bounce']}) — corriger l'adresse pour réactiver les envois`}
        >
          <MailWarning className="h-3 w-3" />
          Email invalide
        </Badge>
      )}
      {/* Dernier appel (si existe) */}
      {lastCall && (
        <>
          <div className="h-4 w-px bg-zinc-200" />
          <div className="flex items-center gap-1">
            <Phone className="h-4 w-4" />
            <span>{formatTimeAgo(lastCall.created_at)}</span>
          </div>
        </>
      )}
      <div className="h-4 w-px bg-zinc-200" />
      <div className="flex items-center gap-1">
        <Globe className="h-4 w-4" />
        <span>{getLocalTime()}</span>
      </div>
    </>
  );

  // Boutons d'action
  const actions = (
    <>
      {/* Bouton d'assignation visible uniquement pour admin et superadmin */}
      {canManageAssignments() && (
        <AssignUserButton
          assignedUserId={contact.assigned_to}
          assignedUserName={
            contact.assigned_user?.full_name || contact.assigned_user?.email || null
          }
          onAssign={handleAssignUser}
          onUnassign={handleUnassignUser}
        />
      )}
      <Button variant="outline" size="sm" onClick={() => setIsCreateTaskModalOpen(true)}>
        Créer une tâche
      </Button>
      {canDeleteEntity('contact') && (
        <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(true)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </>
  );

  return (
    <>
      <DetailPageLayout
        breadcrumbs={breadcrumbs}
        title={`${contact.first_name} ${contact.last_name}`}
        subtitle={subtitle}
        actions={actions}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {/* Contenu onglet Globale */}
        {activeTab === 'globale' && (
          <div className="flex-1 overflow-auto p-6">
            <div className="flex gap-6 h-full">
              {/* Colonne gauche */}
              <div className="flex-1 flex flex-col gap-6">
                {/* Informations générales */}
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-semibold text-zinc-900">Informations générales</p>
                    {isFormDirty && (
                      <Button
                        onClick={() => submitFormRef.current?.()}
                        size="sm"
                        disabled={isFormSubmitting}
                      >
                        {isFormSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Enregistrer
                      </Button>
                    )}
                  </div>
                  <ContactInfoForm
                    contact={contact}
                    onSave={handleUpdateContact}
                    onDirtyChange={setIsFormDirty}
                    onSubmittingChange={setIsFormSubmitting}
                    onSubmitRef={(fn) => {
                      submitFormRef.current = fn;
                    }}
                  />
                </Card>

                {/* Équipages associés */}
                <Card className="flex-1 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-zinc-900">Équipages associés</h2>
                    <button
                      onClick={() => setIsAssignCrewModalOpen(true)}
                      className="flex items-center justify-center size-9 rounded-md bg-zinc-900 hover:bg-zinc-800 transition-colors"
                      aria-label="Assigner un équipage"
                      title="Assigner un équipage"
                    >
                      <Plus className="size-4 text-white" />
                    </button>
                  </div>
                  <CrewsList crews={crews} loading={crewsLoading} />
                </Card>

                {/* Données de formulaire */}
                <FormSubmissionsSection contactId={contact.id} />

                {/* Paiement BPI */}
                <PaymentSection
                  contactId={contact.id}
                  contactName={`${contact.first_name} ${contact.last_name}`}
                  onPaymentConfirmed={async () => {
                    if (refreshTimelineRef.current) await refreshTimelineRef.current();
                  }}
                />
              </div>

              {/* Colonne droite */}
              <div className="w-[38.25rem] flex flex-col gap-6">
                {/* Contact rapide */}
                <ContactCard
                  contact={contact}
                  // TICKET-SEC-07 : sans droit d'envoi CRM, le bouton ouvre la messagerie (mailto).
                  onEmailClick={canSendEmails() ? () => setIsSendEmailModalOpen(true) : undefined}
                  onCallClick={() => setIsLogCallModalOpen(true)}
                />

                {/* Timeline */}
                <Timeline
                  entityType="contact"
                  entityId={contact.id}
                  onEventsLoaded={handleEventsLoaded}
                  onRefreshReady={handleRefreshReady}
                />
              </div>
            </div>
          </div>
        )}

        {/* Contenu onglet Fichiers */}
        {activeTab === 'fichiers' && (
          <div className="flex-1 overflow-auto p-6">
            <FilesList entityType="contact" entityId={contact.id} />
          </div>
        )}
      </DetailPageLayout>

      {/* Confirmation de suppression (CRM-15 : dry-run + garde-fous RPC) */}
      {contact && (
        <ConfirmDeleteContactDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          contactId={contact.id}
          contactName={`${contact.first_name} ${contact.last_name}`}
          onDeleted={() => navigate(ROUTES.DATA.CONTACTS.LIST)}
        />
      )}

      {/* Modale Consigner un appel */}
      <LogCallModal
        isOpen={isLogCallModalOpen}
        onClose={() => setIsLogCallModalOpen(false)}
        entityType="contact"
        entityId={contact.id}
        onSuccess={handleCallSuccess}
      />

      {/* Modale Envoyer un email */}
      <SendEmailModal
        isOpen={isSendEmailModalOpen}
        onClose={() => setIsSendEmailModalOpen(false)}
        entityType="contact"
        entityId={contact.id}
        defaultRecipients={contact.email ? [contact.email] : []}
        onSuccess={handleEmailSuccess}
      />

      {/* Modale Créer une tâche */}
      <CreateTaskModal
        isOpen={isCreateTaskModalOpen}
        onClose={() => setIsCreateTaskModalOpen(false)}
        entityType="contact"
        entityId={contact.id}
        onSuccess={handleTaskSuccess}
      />

      {/* Modale Assigner un équipage */}
      {/* ✅ Optimisation : Ne monter le composant que quand nécessaire */}
      {isAssignCrewModalOpen && (
        <AssignCrewModal
          isOpen={isAssignCrewModalOpen}
          onClose={() => setIsAssignCrewModalOpen(false)}
          onAssign={handleAssignCrew}
        />
      )}
    </>
  );
}
